import { IPageDetails } from '@/shared/services/forms/forms.types';
import {
	IFormFillSubmitResponse,
	IFormSubmission,
	IFormSubmissionAiField,
	IFormSubmissionField,
	IFromSubmissionFieldFileMetadata,
	IFromSubmissionFieldVideoMetadata,
	IGetFormSubmissionsResponse,
	IGetUserSubmissionsResponse,
	ISFormFillSubmitResponse,
	ISFormSubmission,
	ISFormSubmissionAiField,
	ISFormSubmissionField,
	ISFormSubmissionFieldFileMetadata,
	ISFormSubmissionFieldVideoMetadata,
	ISFormSubmissionLastComment,
	ISGetFormSubmissionsResponse,
	ISGetUserSubmissionsResponse,
	ISUploadFileDraftFileMetadata,
	ISUploadFileResponse,
	ISUserSubmission,
	IUploadFileDraftFileMetadata,
	IUploadFileResponse,
	IUserSubmission,
} from '@/shared/services/submissions/submissions.types';

import { JsonValue, transformKeysSnakeToCamelCase } from '@/utils/transformation';

import { getStructuredPageDetails } from '../common';

export function getStructuredFormSubmissionFieldFileMetadata(
	obj: IFromSubmissionFieldFileMetadata
): ISFormSubmissionFieldFileMetadata {
	return {
		contentType: obj.content_type,
		storagePath: obj.storage_path,
		originalFilename: obj.original_filename,
	};
}

export function getStructuredFormSubmissionFieldVideoMetadata(
	obj: IFromSubmissionFieldVideoMetadata
): ISFormSubmissionFieldVideoMetadata {
	return {
		storagePath: obj.storage_path,
		originalFilename: obj.original_filename,
		videoUrl: obj.video_url,
		thumbnail: obj.thumbnail,
		mediaConvertJobId: obj.media_convert_job_id,
		duration: obj.duration,
	};
}

export function getStructuredFormSubmissionField(obj: IFormSubmissionField): ISFormSubmissionField {
	return {
		fieldId: obj.field_id,
		value: obj.value,
		fileMetadata: obj.file_metadata
			? getStructuredFormSubmissionFieldFileMetadata(obj.file_metadata)
			: undefined,
		videoMetadata: obj.video_metadata
			? getStructuredFormSubmissionFieldVideoMetadata(obj.video_metadata)
			: undefined,
		inputType: obj.input_type,
		preDefinedInputType: obj.pre_defined_input_type,
		previewAvailable: obj.preview_available,
		previewUrl: obj.preview_url,
		previewType: obj.preview_type,
	};
}

export function getStructuredFormSubmissionAiField(
	obj: IFormSubmissionAiField
): ISFormSubmissionAiField {
	return {
		id: obj.id,
		fieldId: obj.id,
		reason: obj.reason,
		result: obj.result,
		value: obj.result,
		resultType: obj.result_type,
	};
}

export function getStructuredFormSubmission(obj: IFormSubmission): ISFormSubmission {
	return {
		submissionId: obj.submission_id,
		stage: obj.stage,
		name: obj.name,
		email: obj.email,
		submittedAt: obj.submitted_at,
		aiFieldValues:
			obj.ai_field_values?.map((field) => getStructuredFormSubmissionAiField(field)) ?? [],
		fieldValues: obj.field_values?.map((field) => getStructuredFormSubmissionField(field)) ?? [],
		image: obj.image,
		chatStatus: obj.chat_status,
		responded: obj.responded,
		chatId: obj.chat_id,
		assignedLabels: obj.labels ?? [],
		lastComment: transformKeysSnakeToCamelCase(
			obj?.last_comment as unknown as JsonValue
		) as unknown as ISFormSubmissionLastComment,
		adminCreated: obj.ac === 1,
		hasFeedback: obj.has_feedback,
		avgRating: obj.avg_rating,
		assignmentPendingCount: obj.assignment_pending_count ?? null,
		assignmentSubmittedCount: obj.assignment_submitted_count ?? null,
		assignments: obj.assignments ?? [],
		offer: obj.offer
			? {
					id: obj.offer.id,
					status: obj.offer.status,
					sentAt: obj.offer.sent_at,
					expiresAt: obj.offer.expires_at,
				}
			: null,
		source: obj.source ?? null,
	};
}

export function getStructuredFormSubmissionsResponse(
	obj: IGetFormSubmissionsResponse,
	payloadPageDetails: IPageDetails
): ISGetFormSubmissionsResponse {
	return {
		submissions:
			obj.submissions?.map((submission) => getStructuredFormSubmission(submission)) ?? [],
		pageDetails: getStructuredPageDetails({
			...payloadPageDetails,
			...obj.page_details,
		}),
		statusCounts: obj.status_counts,
		allStagesTotalCount: obj.all_stages_total_count,
	};
}

export function getStructuredUserSubmission(obj: IUserSubmission): ISUserSubmission {
	return {
		submissionDetails: {
			submittedAt: obj.submission_details.submitted_at,
		},
		formDetails: {
			name: obj.form_details.name,
			metaImage: obj.form_details.meta_image,
			image: obj.form_details.image,
		},
	};
}

export function getStructuredUserSubmissions(
	obj: IGetUserSubmissionsResponse,
	payloadPageDetails: IPageDetails
): ISGetUserSubmissionsResponse {
	return {
		submissions:
			obj.submissions?.map((submission) => getStructuredUserSubmission(submission)) ?? [],
		pageDetails: getStructuredPageDetails({
			...payloadPageDetails,
			...obj.page_details,
		}),
	};
}

export function getStructuredUploadFileDraftFileMetadata(
	obj: IUploadFileDraftFileMetadata
): ISUploadFileDraftFileMetadata {
	return {
		contentType: obj.content_type,
		draftFilePath: obj.draft_file_path,
		originalFilename: obj.original_filename,
	};
}

export function getStructuredUploadFileResponse(obj: IUploadFileResponse): ISUploadFileResponse {
	return {
		formId: obj.form_id,
		fieldId: obj.field_id,
		draftFileMetadata: getStructuredUploadFileDraftFileMetadata(obj.draft_file_metadata),
	};
}

export function getStructuredFormFillSubmitResponse(
	obj: IFormFillSubmitResponse
): ISFormFillSubmitResponse {
	return {
		status: obj.status,
		formIdentifier: obj.form_identifier,
	};
}
